import React, { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Icon,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiMonitor, FiSmartphone, FiArrowLeft, FiStar, FiCornerUpLeft, FiMoreVertical } from 'react-icons/fi';
import { buildEmailHtml, resolveTemplateVariables, SAMPLE_VARS } from './buildEmailHtml';

/**
 * Accurate email preview shown as a popup with two device frames:
 *  - Desktop (wide reading pane)
 *  - iPhone Gmail app (narrow, with app chrome)
 *
 * Both frames render the EXACT HTML the recipient will receive (built via
 * buildEmailHtml, which mirrors the server send transforms). The only
 * difference between them is the viewport width, so responsive behavior
 * (max-width images, reflowing tables) renders truthfully.
 */
export default function EmailPreviewModal({
  isOpen,
  onClose,
  body,
  signature,
  variables,
  subject = '(no subject)',
  fromName = 'You',
  fromEmail = '',
  toLabel = 'to me',
}) {
  const labelColor = useColorModeValue('gray.600', 'gray.300');
  const frameBorder = useColorModeValue('gray.200', 'gray.600');
  const sectionBg = useColorModeValue('gray.50', 'gray.800');
  const chromeBg = useColorModeValue('white', 'gray.900');
  const phoneShell = useColorModeValue('gray.900', 'black');
  const subtle = useColorModeValue('gray.500', 'gray.400');
  const avatarBg = useColorModeValue('purple.500', 'purple.400');

  const vars = variables || SAMPLE_VARS;

  const fullHtml = useMemo(() => {
    const resolvedBody = resolveTemplateVariables(body || '', vars);
    return buildEmailHtml(resolvedBody, { signature });
  }, [body, signature, vars]);

  const resolvedSubject = useMemo(
    () => resolveTemplateVariables(subject || '', vars) || '(no subject)',
    [subject, vars]
  );

  const initial = (fromName || 'U').trim().charAt(0).toUpperCase();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', xl: '6xl' }} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="92vh">
        <ModalHeader>
          <HStack spacing={2}>
            <Text>Email Preview</Text>
            <Text fontSize="xs" fontWeight="normal" color={subtle}>
              Exactly what the recipient receives
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Flex direction={{ base: 'column', lg: 'row' }} gap={6} align="flex-start">
            {/* ---------- Desktop ---------- */}
            <Box flex="1" w="100%" minW={0}>
              <HStack mb={2} color={labelColor}>
                <Icon as={FiMonitor} />
                <Text fontSize="sm" fontWeight="semibold">Desktop</Text>
              </HStack>
              <Box borderWidth="1px" borderColor={frameBorder} borderRadius="lg" overflow="hidden" bg={chromeBg}>
                {/* mail header chrome */}
                <Box px={4} pt={3} pb={2} borderBottomWidth="1px" borderColor={frameBorder}>
                  <Text fontSize="lg" fontWeight="semibold" noOfLines={2}>{resolvedSubject}</Text>
                  <HStack mt={2} spacing={3} align="center">
                    <Flex w="36px" h="36px" borderRadius="full" bg={avatarBg} color="white" align="center" justify="center" fontWeight="bold">
                      {initial}
                    </Flex>
                    <Box flex="1" minW={0}>
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                        {fromName}{fromEmail ? ` <${fromEmail}>` : ''}
                      </Text>
                      <Text fontSize="xs" color={subtle}>{toLabel}</Text>
                    </Box>
                    <HStack color={subtle} spacing={3}>
                      <Icon as={FiCornerUpLeft} />
                      <Icon as={FiMoreVertical} />
                    </HStack>
                  </HStack>
                </Box>
                <Box
                  as="iframe"
                  title="Desktop email preview"
                  srcDoc={fullHtml}
                  sandbox="allow-same-origin"
                  w="100%"
                  h="560px"
                  border="0"
                  bg="white"
                />
              </Box>
            </Box>

            {/* ---------- iPhone (Gmail app) ---------- */}
            <Box flexShrink={0} w={{ base: '100%', lg: '380px' }}>
              <HStack mb={2} color={labelColor}>
                <Icon as={FiSmartphone} />
                <Text fontSize="sm" fontWeight="semibold">iPhone — Gmail app</Text>
              </HStack>
              <Box
                mx="auto"
                w="375px"
                maxW="100%"
                bg={phoneShell}
                borderRadius="40px"
                p="10px"
                boxShadow="xl"
              >
                <Box bg={chromeBg} borderRadius="30px" overflow="hidden" position="relative">
                  {/* status bar */}
                  <Flex h="34px" align="center" justify="space-between" px={5} pt={1} fontSize="xs" fontWeight="semibold">
                    <Text>9:41</Text>
                    <Box w="120px" h="22px" bg={phoneShell} borderRadius="full" /> {/* notch */}
                    <Text>5G</Text>
                  </Flex>
                  {/* gmail app bar */}
                  <HStack px={3} py={2} spacing={3} borderBottomWidth="1px" borderColor={frameBorder}>
                    <Icon as={FiArrowLeft} boxSize={5} color={subtle} />
                    <Box flex="1" />
                    <Icon as={FiStar} boxSize={5} color={subtle} />
                    <Icon as={FiMoreVertical} boxSize={5} color={subtle} />
                  </HStack>
                  {/* subject + sender */}
                  <Box px={4} pt={3} pb={2}>
                    <Text fontSize="md" fontWeight="semibold" noOfLines={3}>{resolvedSubject}</Text>
                    <HStack mt={2} spacing={2} align="center">
                      <Flex w="32px" h="32px" borderRadius="full" bg={avatarBg} color="white" align="center" justify="center" fontWeight="bold" fontSize="sm">
                        {initial}
                      </Flex>
                      <Box flex="1" minW={0}>
                        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{fromName}</Text>
                        <Text fontSize="xs" color={subtle} noOfLines={1}>{toLabel}</Text>
                      </Box>
                    </HStack>
                  </Box>
                  {/* email body at phone width */}
                  <Box
                    as="iframe"
                    title="iPhone email preview"
                    srcDoc={fullHtml}
                    sandbox="allow-same-origin"
                    w="100%"
                    h="560px"
                    border="0"
                    bg="white"
                  />
                </Box>
              </Box>
            </Box>
          </Flex>
          <Text mt={4} fontSize="xs" color={subtle}>
            Images load from their hosted URLs exactly as your recipient&apos;s mail client will load them. Note: Outlook
            desktop ignores centered-image margins and may left-align centered images.
          </Text>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
